import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import {
    Alert,
    alpha,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { getApiBaseUrl } from "../../../config/api";
import { getSchoolIdForUser } from "../../../utils/userContext";
import { getAllSclasses } from "../../../redux/sclassRelated/sclassHandle";
import { useTeacherTeaching } from "../../../hooks/useTeacherTeaching";
import { getSubjectId } from "../../../utils/teacherSubjects";
import Popup from "../../../components/Popup";
import ConfirmDeleteDialog from "../../../components/ConfirmDeleteDialog";
import {
    CA_TYPES,
    createCaAssessment,
    deleteCaAssessment,
    fetchCaAssessments,
    fetchClassCaSummary,
    updateCaAssessment,
} from "../../../utils/caAssessmentApi";
import {
    TermRecordsScopeAlert,
    TermRecordsScopeTabs,
} from "../../../components/term/TermRecordsScopeTabs";
import { useTermRecordsScope } from "../../../hooks/useTermRecordsScope";
import { buildTermRecordsQuery, TERM_RECORDS_SCOPES } from "../../../utils/termRecordsScope";

const TERMS = ["Term 1", "Term 2", "Term 3"];

const EMPTY_FORM = {
    studentId: "",
    assessmentType: "Class test",
    title: "",
    description: "",
    marks: "",
    maxMarks: "100",
    weight: "20",
    rubricCriteria: "",
    portfolioNote: "",
};

function CaAssessmentManagement({ teacherMode = false }) {
    const dispatch = useDispatch();
    const { currentUser } = useSelector((state) => state.user);
    const { sclassesList } = useSelector((state) => state.sclass);
    const teaching = useTeacherTeaching();

    const schoolId = getSchoolIdForUser(currentUser);
    const teacherId = currentUser?._id || currentUser?.id;
    const { recordsScope, setRecordsScope, currentTermLabel, currentTerm, currentAcademicYear } =
        useTermRecordsScope(schoolId);

    const [classId, setClassId] = useState("");
    const [subjectId, setSubjectId] = useState("");
    const [term, setTerm] = useState("Term 1");
    const [academicYear, setAcademicYear] = useState(String(new Date().getFullYear()));
    const [records, setRecords] = useState([]);
    const [summary, setSummary] = useState([]);
    const [students, setStudents] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [message, setMessage] = useState("");
    const [showPopup, setShowPopup] = useState(false);

    useEffect(() => {
        if (!teacherMode && schoolId) {
            dispatch(getAllSclasses(schoolId, "Sclass"));
        }
    }, [dispatch, schoolId, teacherMode]);

    useEffect(() => {
        if (recordsScope === TERM_RECORDS_SCOPES.CURRENT && currentTerm) {
            setTerm(currentTerm);
        }
        if (recordsScope === TERM_RECORDS_SCOPES.CURRENT && currentAcademicYear) {
            setAcademicYear(currentAcademicYear);
        }
    }, [recordsScope, currentTerm, currentAcademicYear]);

    useEffect(() => {
        if (teacherMode && teaching.activeClassId) {
            setClassId(String(teaching.activeClassId));
            setSubjectId(String(teaching.activeSubjectId || ""));
        }
    }, [teacherMode, teaching.activeClassId, teaching.activeSubjectId]);

    const classOptions = useMemo(() => {
        if (teacherMode && teaching.activeClass) {
            return [teaching.activeClass];
        }
        return Array.isArray(sclassesList) ? sclassesList : [];
    }, [teacherMode, teaching.activeClass, sclassesList]);

    const loadClassMeta = useCallback(async () => {
        if (!classId) {
            setStudents([]);
            setSubjects([]);
            return;
        }
        try {
            const base = getApiBaseUrl();
            const [studentsRes, classRes] = await Promise.all([
                axios.get(`${base}/Sclass/Students/${classId}`),
                axios.get(`${base}/Sclass/${classId}`),
            ]);
            setStudents(Array.isArray(studentsRes.data) ? studentsRes.data : []);
            const subs = classRes.data?.subjects || classRes.data?.Subjects || [];
            setSubjects(Array.isArray(subs) ? subs : []);
            if (!subjectId && subs.length) {
                setSubjectId(String(subs[0]._id || subs[0].id));
            }
        } catch {
            setStudents([]);
            setSubjects([]);
        }
    }, [classId, subjectId]);

    const loadRecords = useCallback(async () => {
        if (!schoolId || !classId) {
            setRecords([]);
            setSummary([]);
            return;
        }
        setLoading(true);
        try {
            const filters = buildTermRecordsQuery({
                recordsScope,
                extra: {
                    classId,
                    ...(subjectId ? { subjectId } : {}),
                },
            });
            const summaryParams =
                recordsScope === TERM_RECORDS_SCOPES.PAST
                    ? { classId, term, academicYear }
                    : { classId, term: currentTerm || term, academicYear: currentAcademicYear || academicYear };
            const [rows, summaryData] = await Promise.all([
                fetchCaAssessments(schoolId, filters),
                fetchClassCaSummary(summaryParams),
            ]);
            setRecords(rows);
            setSummary(Array.isArray(summaryData?.summary) ? summaryData.summary : []);
        } catch (err) {
            setMessage(err.response?.data?.message || err.message || "Could not load CA records.");
            setShowPopup(true);
            setRecords([]);
            setSummary([]);
        } finally {
            setLoading(false);
        }
    }, [schoolId, classId, subjectId, term, academicYear, recordsScope, currentTerm, currentAcademicYear]);

    useEffect(() => {
        loadClassMeta();
    }, [loadClassMeta]);

    useEffect(() => {
        loadRecords();
    }, [loadRecords]);

    const studentName = (id) => {
        const s = students.find((x) => String(x._id || x.id) === String(id));
        return s ? `${s.name} (${s.rollNum})` : id;
    };

    const openCreate = () => {
        setEditing(null);
        setForm({ ...EMPTY_FORM, studentId: students[0]?._id || students[0]?.id || "" });
        setDialogOpen(true);
    };

    const openEdit = (row) => {
        setEditing(row);
        setForm({
            studentId: row.studentId,
            assessmentType: row.assessmentType || "Class test",
            title: row.title || "",
            description: row.description || "",
            marks: String(row.marks ?? ""),
            maxMarks: String(row.maxMarks ?? 100),
            weight: String(row.weight ?? 20),
            rubricCriteria: row.rubricCriteria || "",
            portfolioNote: row.portfolioNote || "",
        });
        setDialogOpen(true);
    };

    const handleSave = async () => {
        if (!form.studentId) {
            setMessage("Select a student.");
            setShowPopup(true);
            return;
        }
        setSaving(true);
        try {
            const payload = {
                schoolId,
                studentId: form.studentId,
                subjectId: subjectId || "",
                classId,
                term,
                academicYear,
                assessmentType: form.assessmentType,
                title: form.title,
                description: form.description,
                marks: form.marks,
                maxMarks: form.maxMarks,
                weight: form.weight,
                rubricCriteria: form.rubricCriteria,
                portfolioNote: form.portfolioNote,
                enteredByTeacherId: teacherId,
            };
            if (editing) {
                await updateCaAssessment(editing._id || editing.id, payload);
            } else {
                await createCaAssessment(payload);
            }
            setDialogOpen(false);
            setMessage(editing ? "CA record updated." : "CA record added.");
            setShowPopup(true);
            await loadRecords();
        } catch (err) {
            setMessage(err.response?.data?.message || err.message || "Could not save.");
            setShowPopup(true);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            await deleteCaAssessment(deleteTarget._id || deleteTarget.id);
            setDeleteTarget(null);
            setMessage("CA record deleted.");
            setShowPopup(true);
            await loadRecords();
        } catch (err) {
            setMessage(err.response?.data?.message || err.message || "Could not delete.");
            setShowPopup(true);
        }
    };

    return (
        <Box
            sx={{
                minHeight: "100%",
                py: { xs: 2, sm: 3 },
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.05)} 0%, ${t.palette.grey[50]} 24%)`
                        : alpha(t.palette.common.black, 0.08),
            }}
        >
            <Container maxWidth="lg">
                <Stack spacing={2} sx={{ mb: 3 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <AssessmentOutlinedIcon color="primary" />
                        <Typography variant="h4" fontWeight={800}>
                            Continuous Assessment (CA)
                        </Typography>
                    </Stack>
                    <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 760 }}>
                        Record class tests, assignments, portfolios, and rubrics. CA contributes 40% of the
                        weighted term average (exam 60%) for automated promotion suggestions aligned with Malawi
                        curriculum practice.
                    </Typography>
                </Stack>

                <TermRecordsScopeTabs value={recordsScope} onChange={setRecordsScope} />
                <TermRecordsScopeAlert
                    scope={recordsScope}
                    currentTermLabel={currentTermLabel}
                    sectionLabel="CA records"
                />

                <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, mb: 2 }}>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        {!teacherMode ? (
                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Class</InputLabel>
                                    <Select
                                        label="Class"
                                        value={classId}
                                        onChange={(e) => {
                                            setClassId(e.target.value);
                                            setSubjectId("");
                                        }}
                                    >
                                        {classOptions.map((c) => (
                                            <MenuItem key={c._id || c.id} value={c._id || c.id}>
                                                {c.sclassName}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        ) : (
                            <Grid item xs={12} sm={6} md={3}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    label="Class"
                                    value={teaching.className}
                                    InputProps={{ readOnly: true }}
                                />
                            </Grid>
                        )}
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Subject</InputLabel>
                                <Select
                                    label="Subject"
                                    value={subjectId}
                                    onChange={(e) => setSubjectId(e.target.value)}
                                    disabled={teacherMode}
                                >
                                    {(teacherMode && teaching.activeSubject
                                        ? [teaching.activeSubject]
                                        : subjects
                                    ).map((s) => (
                                        <MenuItem key={getSubjectId(s)} value={getSubjectId(s)}>
                                            {s.subName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} sm={4} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Term</InputLabel>
                                <Select label="Term" value={term} onChange={(e) => setTerm(e.target.value)}>
                                    {TERMS.map((t) => (
                                        <MenuItem key={t} value={t}>
                                            {t}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} sm={4} md={2}>
                            <TextField
                                size="small"
                                fullWidth
                                label="Academic year"
                                value={academicYear}
                                onChange={(e) => setAcademicYear(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4} md={2}>
                            <Button
                                fullWidth
                                variant="contained"
                                startIcon={<AddIcon />}
                                disabled={!classId || !students.length}
                                onClick={openCreate}
                                sx={{ height: 40 }}
                            >
                                Add CA
                            </Button>
                        </Grid>
                    </Grid>

                    {loading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            {summary.length > 0 ? (
                                <>
                                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                                        Class CA summary ({term})
                                    </Typography>
                                    <TableContainer sx={{ mb: 3, maxHeight: 200 }}>
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Student</TableCell>
                                                    <TableCell align="right">Assessments</TableCell>
                                                    <TableCell align="right">CA average %</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {summary.map((row) => (
                                                    <TableRow key={row.studentId}>
                                                        <TableCell>{studentName(row.studentId)}</TableCell>
                                                        <TableCell align="right">{row.assessmentCount}</TableCell>
                                                        <TableCell align="right">
                                                            {row.caAverage != null
                                                                ? `${Number(row.caAverage).toFixed(1)}%`
                                                                : "—"}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </>
                            ) : null}

                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Student</TableCell>
                                            <TableCell>Type</TableCell>
                                            <TableCell>Title</TableCell>
                                            <TableCell align="right">Marks</TableCell>
                                            <TableCell align="right">Weight %</TableCell>
                                            <TableCell align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {records.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                                    No CA records for this filter. Add class tests, assignments,
                                                    portfolios, or rubrics.
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            records.map((row) => (
                                                <TableRow key={row._id || row.id} hover>
                                                    <TableCell>{studentName(row.studentId)}</TableCell>
                                                    <TableCell>
                                                        <Chip size="small" label={row.assessmentType} />
                                                    </TableCell>
                                                    <TableCell>{row.title}</TableCell>
                                                    <TableCell align="right">
                                                        {row.marks}/{row.maxMarks}
                                                    </TableCell>
                                                    <TableCell align="right">{row.weight}%</TableCell>
                                                    <TableCell align="right">
                                                        <Button
                                                            size="small"
                                                            startIcon={<EditOutlinedIcon />}
                                                            onClick={() => openEdit(row)}
                                                        >
                                                            Edit
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            color="error"
                                                            startIcon={<DeleteOutlineIcon />}
                                                            onClick={() => setDeleteTarget(row)}
                                                        >
                                                            Delete
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}
                </Paper>

                <Alert severity="info" sx={{ borderRadius: 2 }}>
                    Weighted term mark = 40% CA average + 60% end-of-term exam average. Use{" "}
                    <strong>Apply suggestions</strong> on the promotion review screen to auto-fill pass/fail from
                    these marks.
                </Alert>
            </Container>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editing ? "Edit CA record" : "Add CA record"}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Student</InputLabel>
                            <Select
                                label="Student"
                                value={form.studentId}
                                onChange={(e) => setForm((f) => ({ ...f, studentId: e.target.value }))}
                            >
                                {students.map((s) => (
                                    <MenuItem key={s._id || s.id} value={s._id || s.id}>
                                        {s.rollNum} — {s.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small">
                            <InputLabel>Assessment type</InputLabel>
                            <Select
                                label="Assessment type"
                                value={form.assessmentType}
                                onChange={(e) => setForm((f) => ({ ...f, assessmentType: e.target.value }))}
                            >
                                {CA_TYPES.map((t) => (
                                    <MenuItem key={t} value={t}>
                                        {t}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            size="small"
                            label="Title"
                            fullWidth
                            value={form.title}
                            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                        />
                        <TextField
                            size="small"
                            label="Description (optional)"
                            fullWidth
                            multiline
                            minRows={2}
                            value={form.description}
                            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                        />
                        <Stack direction="row" spacing={2}>
                            <TextField
                                size="small"
                                label="Marks"
                                type="number"
                                value={form.marks}
                                onChange={(e) => setForm((f) => ({ ...f, marks: e.target.value }))}
                                sx={{ flex: 1 }}
                            />
                            <TextField
                                size="small"
                                label="Out of"
                                type="number"
                                value={form.maxMarks}
                                onChange={(e) => setForm((f) => ({ ...f, maxMarks: e.target.value }))}
                                sx={{ flex: 1 }}
                            />
                            <TextField
                                size="small"
                                label="Weight %"
                                type="number"
                                value={form.weight}
                                onChange={(e) => setForm((f) => ({ ...f, weight: e.target.value }))}
                                sx={{ flex: 1 }}
                            />
                        </Stack>
                        {form.assessmentType === "Rubric" ? (
                            <TextField
                                size="small"
                                label="Rubric criteria"
                                fullWidth
                                multiline
                                minRows={2}
                                value={form.rubricCriteria}
                                onChange={(e) => setForm((f) => ({ ...f, rubricCriteria: e.target.value }))}
                            />
                        ) : null}
                        {form.assessmentType === "Portfolio" ? (
                            <TextField
                                size="small"
                                label="Portfolio note"
                                fullWidth
                                multiline
                                minRows={2}
                                value={form.portfolioNote}
                                onChange={(e) => setForm((f) => ({ ...f, portfolioNote: e.target.value }))}
                            />
                        ) : null}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" disabled={saving} onClick={handleSave}>
                        {saving ? "Saving…" : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDeleteDialog
                open={Boolean(deleteTarget)}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleDelete}
                title="Delete CA record?"
                message="This continuous assessment entry will be removed permanently."
            />

            <Popup message={message} setShowPopup={setShowPopup} showPopup={showPopup} />
        </Box>
    );
}

export default CaAssessmentManagement;
